import { LineGlobe } from '@qr-front/icons/line-globe'
import { LineShareAndroid } from '@qr-front/icons/line-share-android'
import { Button, buttonCls } from '@qr-front/ui/components/Button'
import { Tooltip } from '@qr-front/ui/components/Tooltip'
import { cn } from '@qr-front/ui/utils'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'

import { useFrameFontSize } from '@/generator/client'
import { getContrastingNeutralColor } from '@/utils'

import { addMissingUrlProtocol } from '../../../../../utils/urlUtils'
import { ShareQrDialog } from '../../../components/ShareQrDialog'
import {
	imagesInfoButtonListAtom,
	imagesInfoDescriptionAtom,
	imagesInfoHeadlineAtom,
	imagesInfoWebsiteAtom,
	shareImageGalleryAtom,
} from '../../../contentFormSections'
import { TImagesButton } from '../../../contentFormSections/ImagesInfoForm/imagesInfoFormModel'
import { openOverlayIdAtom, qrUriAtom } from '../../../generatorState'
import { QrScreenOverlay } from '../../../QrScreen'
import { QrScreen, TQrScreenProps } from '../../../QrScreen/QrScreen'
import { useColoredButton } from '../../hooks/useColoredButton'
import { ImageGallery } from './ImageGallery'
import { ImagesPreview } from './ImagesPreview'
import {
	imagesPrimaryColorAtom,
	imagesSecondaryColorAtom,
} from './imagesScreenState'

const Header = ({ demoMode }: TQrScreenProps) => {
	const t = useTranslations()

	const primaryColor = useAtomValue(imagesPrimaryColorAtom)
	const primaryColorContrastingColor = getContrastingNeutralColor(primaryColor)
	const headline = useAtomValue(imagesInfoHeadlineAtom)
	const description = useAtomValue(imagesInfoDescriptionAtom)
	const displayedDescription = demoMode
		? t('generator__images_screen__description_fallback')
		: description
	const website = useAtomValue(imagesInfoWebsiteAtom)
	const displayedWebsite = demoMode
		? t('generator__images_screen__website_fallback')
		: website

	return (
		<div
			className="flex flex-col items-center px-5 py-6 transition-colors word-break-fix"
			style={{
				backgroundColor: primaryColor,
			}}
		>
			<h1
				className="type-heading-4 text-center transition-colors"
				style={{
					color: primaryColorContrastingColor,
				}}
			>
				{headline || t('generator__images_screen__headline_fallback')}
			</h1>
			{displayedDescription ? (
				<p
					className="text-center text-[10px] leading-[16px] transition-colors"
					style={{
						color: primaryColorContrastingColor,
					}}
				>
					{displayedDescription}
				</p>
			) : null}
			{displayedWebsite ? (
				<a
					href={addMissingUrlProtocol(displayedWebsite)}
					target="_blank"
					className="mt-4 flex w-fit items-center gap-2 text-center text-[10px] leading-[16px] transition-colors"
					style={{
						color: primaryColorContrastingColor,
					}}
					rel="noreferrer"
				>
					<LineGlobe className="size-4" />
					{displayedWebsite}
				</a>
			) : null}
		</div>
	)
}

const ShareButton = ({
	small,
	previewMode,
	demoMode,
}: {
	small: boolean
	previewMode: boolean
	demoMode: boolean
}) => {
	const t = useTranslations()

	const secondaryColor = useAtomValue(imagesSecondaryColorAtom)
	const buttonColor = demoMode ? '#FFFFFF' : secondaryColor
	const qrUri = useAtomValue(qrUriAtom)
	const [showShareDialog, setShowShareDialog] = useState(false)

	const { coloredButtonCls, coloredButtonStyle } = useColoredButton(buttonColor)

	const buttonRef = useRef<HTMLButtonElement>(null)

	return (
		<>
			<ShareQrDialog
				isOpen={showShareDialog}
				setIsOpen={setShowShareDialog}
				previewMode={previewMode}
			/>
			{!qrUri ? (
				<Tooltip anchorRef={buttonRef}>
					{t(
						'generator__content_form_section__images_information__share_button_tooltip',
					)}
				</Tooltip>
			) : null}
			<Button
				ref={buttonRef}
				visual="secondary"
				onClick={() => {
					if (qrUri) {
						setShowShareDialog(true)
					}
				}}
				className={cn(
					coloredButtonCls,
					small ? 'w-10 p-0' : 'w-full',
					'shrink-0 rounded-6',
				)}
				style={coloredButtonStyle}
			>
				<LineShareAndroid className="size-4" />
			</Button>
		</>
	)
}

const CustomButton = ({
	button: { title, url },
	previewMode,
	demoMode,
}: {
	button: TImagesButton
	previewMode: boolean
	demoMode: boolean
}) => {
	const t = useTranslations()

	const secondaryColor = useAtomValue(imagesSecondaryColorAtom)

	const { coloredButtonCls, coloredButtonStyle } =
		useColoredButton(secondaryColor)

	const fontSize = useFrameFontSize({
		text: title,
		maxWidth: previewMode || demoMode ? 130 : 200,
		maxSize: previewMode || demoMode ? 12 : 15,
		fontFamily: 'var(--font-body)',
	})

	const baseButtonCls = cn(
		buttonCls({ visual: 'secondary' }),
		'flex min-h-10 min-w-[172px] flex-1 grow text-body-caption font-normal',
		'flex min-w-[172px] flex-1 grow text-body-caption font-normal',
		'rounded-6',
	)

	const style = {
		fontSize: `${fontSize}px`,
		...coloredButtonStyle,
	}

	return (
		<a
			href={addMissingUrlProtocol(url || '')}
			target="_blank"
			className={cn(baseButtonCls, coloredButtonCls)}
			style={style}
			rel="noreferrer"
		>
			{title ||
				t(
					'generator__content_form_section__images_information__button_text__placeholder',
				)}
		</a>
	)
}

const DemoButton = () => {
	const t = useTranslations()

	const { coloredButtonCls, coloredButtonStyle } = useColoredButton('black')

	return (
		<div
			className={cn(
				buttonCls({ visual: 'secondary' }),
				coloredButtonCls,
				'grow text-body-caption font-normal',
				'rounded-6',
			)}
			style={coloredButtonStyle}
		>
			{t('generator__images_screen__demo_mode_button_placeholder')}
		</div>
	)
}

const Buttons = ({
	previewMode,
	demoMode,
}: {
	previewMode: boolean
	demoMode: boolean
}) => {
	const showShareButton = useAtomValue(shareImageGalleryAtom) || demoMode
	const buttonList = useAtomValue(imagesInfoButtonListAtom)
	const firstButton = buttonList[0]
	const additionalButtonList = buttonList.slice(1)

	return (
		<div className="mx-auto mt-6 flex max-w-qr-screen-constrained flex-col gap-2 px-5">
			<div className="flex gap-2">
				{demoMode && <DemoButton />}
				{firstButton && (
					<CustomButton
						button={firstButton}
						previewMode={previewMode}
						demoMode={demoMode}
					/>
				)}
				{showShareButton && (
					<ShareButton
						small={!!firstButton || demoMode}
						previewMode={previewMode}
						demoMode={demoMode}
					/>
				)}
			</div>
			{additionalButtonList.map((button, index) => (
				<CustomButton
					key={index}
					button={button}
					previewMode={previewMode}
					demoMode={demoMode}
				/>
			))}
		</div>
	)
}

const Content = ({ previewMode, demoMode }: TQrScreenProps) => {
	const setOpenOverlayId = useSetAtom(openOverlayIdAtom)

	return (
		<>
			<QrScreenOverlay
				id="image-gallery"
				previewMode={previewMode}
				className="z-[1] bg-generator-bg"
			>
				<ImageGallery hideOverlay={() => setOpenOverlayId(null)} />
			</QrScreenOverlay>
			<div className="pb-6">
				<Header previewMode={previewMode} demoMode={demoMode} />
				<ImagesPreview
					demoMode={demoMode}
					showOverlay={() => {
						setOpenOverlayId('image-gallery')
					}}
				/>

				<Buttons previewMode={previewMode} demoMode={demoMode} />
			</div>
		</>
	)
}

export const ImagesScreen = (props: TQrScreenProps) => (
	<QrScreen
		{...props}
		primaryColor={useAtomValue(imagesPrimaryColorAtom)}
		content={<Content {...props} />}
	/>
)
